import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { z } from 'zod';
import { loadConfig, shown } from './config.ts';
import './styles.css';

// The CSP forbids eval; without this zod probes for it and logs a violation on every page.
z.config({ jitless: true });

// The offline shell (FR-89): the app's own files stay on the phone, so it opens with no signal. Production only.
// The browser looks for a new build on each load; a phone left open for days looks when it is picked up, hourly at most.
// A stage hiding `offline` never installs it, so nothing stays on a phone to take back.
function offlineShell() {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator) || !shown('offline')) return;
  navigator.serviceWorker.register('/sw.js').then((registration) => {
    let checked = Date.now();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible' || Date.now() - checked < 3_600_000) return;
      checked = Date.now();
      registration.update().catch(() => { /* no signal: the next one */ });
    });
  }).catch(() => { /* the app works without it, only not offline */ });
}

const root = createRoot(document.getElementById('root')!);

loadConfig()
  .then(async () => {
    offlineShell();
    const { reportUncaughtErrors } = await import('./report-error.ts');
    reportUncaughtErrors();
    const { App } = await import('./app.tsx');
    root.render(<StrictMode><App /></StrictMode>);
  })
  .catch((e: unknown) => {
    root.render(
      <div className="center-screen">
        <div className="banner banner-bad">The app could not start: {e instanceof Error ? e.message : String(e)}</div>
      </div>,
    );
  });
